import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ZefSnackService } from '@zerops/zef/snack';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import {
  resendVerificationEmail,
  resendVerificationEmailSuccess,
  resendVerificationEmailFail,
  resendVerificationEmailAlreadyVerified
} from './email-verification-notification.action';
import { EmailVerificationNotificationApi } from './email-verification-notification.api';
import { EMAIL_ALREADY_VERIFIED_ERROR_CODE } from './email-verification-notification.constant';

@Injectable()
export class EmailVerificationNotificationEffect {

  onResend$ = createEffect(() => this._actions$.pipe(
    ofType(resendVerificationEmail),
    switchMap((action) => this._api
      .resend$(action.data.id)
      .pipe(
        map((response) => resendVerificationEmailSuccess(response, action)),
        catchError((err) => of(
          err?.code === EMAIL_ALREADY_VERIFIED_ERROR_CODE
            ? resendVerificationEmailAlreadyVerified({ id: action.data.id }, action)
            : resendVerificationEmailFail(err, action)
        ))
      )
    )
  ));

  /**
   * The e-mail is already verified on the backend, but there is no WS channel for the
   * user entity (missing clientId context), so the locally cached `emailVerified: false`
   * would keep the "verify your e-mail" warning visible. Patch the cache directly.
   */
  onAlreadyVerifiedSyncUser$ = createEffect(() => this._actions$.pipe(
    ofType(resendVerificationEmailAlreadyVerified),
    map(({ data }) => this._userEntity.updateCache([ {
      id: data.id,
      emailVerified: true
    } ]))
  ));

  onResendSuccessNotification$ = createEffect(() => this._actions$.pipe(
    ofType(resendVerificationEmailSuccess),
    switchMap(() => this._snack.success$({
      text: 'Verification e-mail sent. Please check your inbox.'
    }))
  ), { dispatch: false });

  onAlreadyVerifiedNotification$ = createEffect(() => this._actions$.pipe(
    ofType(resendVerificationEmailAlreadyVerified),
    switchMap(() => this._snack.success$({
      text: 'Your e-mail address is already verified.'
    }))
  ), { dispatch: false });

  constructor(
    private _actions$: Actions,
    private _api: EmailVerificationNotificationApi,
    private _snack: ZefSnackService,
    private _userEntity: UserEntity
  ) { }

}
